(ns app.front.view.complaint.search
  (:require [app.domain.complaint.model :as complaint-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.common-tags :as ct]
            [app.front.ui.error-boundary :as eb]
            [app.front.ui.filter-common :as filter-common]
            [app.front.util :refer [for-frag]]
            [app.query.filter-util :as filter-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))


(def page-limit 50)


(rf/reg-event-fx ::init
  (fn [{:keys [db]} [_ authority-group-ids]]
    {:comm/command {:command          :complaint/search-config
                    :method           :get
                    :data             {:authority-group-ids authority-group-ids}
                    :on-success       [::search-config.success]
                    :finally-dispatch [::search-config.done]}
     :db           (-> (update db ::data (fn [data]
                                           (-> (assoc data ::loading? true ::authority-group-ids authority-group-ids)
                                               (update-in [::search-config ::filter-values]
                                                          (fnil identity {:domain/state #{:complaint.state/open}})))))
                       (assoc ::sort-condition :search-sorting/created-asc
                              ::current-count page-limit))}))


(defn default-data-map [search-config]
  {::filter-values (-> search-config :default-selected-values)})


(defn set-search-config [db response]
  (-> (assoc-in db [::data ::search-config] (dissoc response :user/complaint-filters))
      (update ::data merge {::revision 0} (default-data-map response))))


(rf/reg-event-fx ::search-config.success
  (fn [{:keys [db]} [_ status response]]
    {:db (set-search-config db response)
     :fx [[:dispatch [::search]]
          [:dispatch [::entities/merge-entity-db status (:user/complaint-filters response)]]]}))


(rf/reg-event-db ::search-config.done
  #(assoc-in % [::data ::loading?] false))


(rf/reg-event-fx ::clear-search
  (fn [{:keys [db]} _]
    {:db       (-> (p/dissoc-in db [::data ::search-term])
                   (assoc-in [::data ::filter-values] {})
                   (update-in [::data ::revision] inc))
     :dispatch [::search]}))


(rf/reg-event-fx ::set-filter-value
  (fn [{:keys [db]} [_ attr value]]
    {:db       (if (seq value)
                 (assoc-in db [::data ::filter-values attr] value)
                 (p/dissoc-in db [::data ::filter-values attr]))
     :dispatch [::search]}))


(rf/reg-event-fx ::select-saved-filter
  (fn [{:keys [db]} [_ filter]]
    {:db       (-> (assoc-in db [::data ::filter-values] (filter-util/filter-entity->search-values-map filter))
                   (update-in [::data ::revision] inc))
     :dispatch [::search]}))


(defn format-search-request [db]
  (p/assoc-if (merge {:conditions (->> db
                                       ::data
                                       ::filter-values
                                       filter-common/filter-values->conditions)}
                     (select-keys (->> db ::data) [:sort-condition :skip :limit]))
              :search-term (-> db ::data ::search-term)))


(rf/reg-event-fx ::save-filter
  (fn [{:keys [db]} [_ filter-name]]
    {:comm/command {:command          :user/upsert-complaint-filter
                    :data             {:filter/name       filter-name
                                       :filter/conditions (->> (format-search-request db)
                                                               :conditions)}
                    :on-success       [::save-filter.success]
                    :finally-dispatch [::save-filter.done]}
     :db           (assoc-in db [::data ::saving-filter?] true)}))


(rf/reg-event-fx ::save-filter.success
  (fn [_ [_ status response]]
    {:fx [[:dispatch [::entities/apply-changes status response]]
          [:dispatch [:notifications/add :toast :success :success :dashboard-search/save-success]]]}))


(rf/reg-event-db ::save-filter.done
  #(p/dissoc-in % [::data ::saving-filter?]))


(rf/reg-event-fx ::save-quick-filter
  (fn [{:keys [db]} [_ idx filter-id existing-filter-id]]

    {:db           (cond-> db
                     filter-id (assoc-in [::data ::search-config :user/complaint-quick-filters filter-id] 0))
     :comm/command {:command    (if filter-id
                                  :user/save-quick-filter
                                  :user/remove-quick-filter)
                    :data       (merge {:filter/subtype :filter.subtype/complaint}
                                       (if filter-id
                                         {:id                  filter-id
                                          :ordered/order       idx
                                          :authority-group-ids (-> db ::data ::authority-group-ids)}
                                         ; Remove existing placement
                                         {:id existing-filter-id}))
                    :on-success [::save-quick-filter.success]}
     :fx           (concat (when filter-id
                             [[:dispatch [::entities/update-entity filter-id {:ordered/order idx}]]])
                           (when existing-filter-id
                             [[:dispatch [::entities/update-entity existing-filter-id {:ordered/order nil}]]]))}))


(rf/reg-event-fx ::save-quick-filter.success
  (fn [{:keys [db]} [_ status {:keys [id total tx-changes remove-id]}]]
    {:db (cond-> db
           total (assoc-in [::data ::search-config :user/complaint-quick-filters id] total)
           remove-id (p/dissoc-in [::data ::search-config :user/complaint-quick-filters remove-id]))
     :fx [[:dispatch [::entities/apply-changes status tx-changes]]
          [:dispatch [:notifications/add :toast :success :success :dashboard-search/save-success]]]}))


(rf/reg-event-fx
  ::delete-filter
  (fn [_ [_ filter-id]]
    {:comm/command {:command    :user/remove-complaint-filter
                    :data       {:id filter-id}
                    :on-success [::delete-filter.success]}}))


(rf/reg-event-fx
  ::delete-filter.success
  (fn [_ [_ status response]]
    {:fx [[:dispatch [::clear-search]]
          [:dispatch [::entities/apply-changes status response]]
          [:dispatch [:notifications/add :toast :success :success :dashboard-search/delete-success]]]}))


(rf/reg-sub ::data
  :-> ::data)


(rf/reg-sub ::search-config
  :<- [::data]
  :-> ::search-config)


(rf/reg-sub ::filter-values
  :<- [::data]
  :-> ::filter-values)


(rf/reg-sub ::revision
  :<- [::data]
  :-> ::revision)


(rf/reg-sub ::user-filters
  :<- [::entities/context]
  (fn [ctx _]
    (->> (entities/find-entities ctx {:entity/type    :entity.type/filter
                                      :filter/subtype :filter.subtype/complaint})
         (sort-by :filter/name))))


(rf/reg-sub ::saving-filter?
  :<- [::data]
  #(-> % ::saving-filter? boolean))


(rf/reg-sub ::config-loading?
  :<- [::data]
  #(-> % ::loading? boolean))


(rf/reg-sub ::search-in-progress?
  :<- [::data]
  #(-> % ::search-in-progress? boolean))


(rf/reg-sub ::selected-saved-filter
  :<- [::user-filters]
  :<- [::filter-values]
  (fn [[user-filters filter-values]]
    (-> user-filters
        (p/find-first #(= (filter-util/filter-entity->search-values-map %)
                          filter-values)))))


(rf/reg-sub ::user-quick-filters
  :<- [::search-config]
  :<- [::user-filters]
  (fn [[search-config filters]]
    (->> search-config
         :user/complaint-quick-filters
         (map (fn [[id _]]
                [(p/find-first filters {:db/id id})
                 (get-in search-config [:user/complaint-quick-filters id])]))
         (sort-by (comp :ordered/order first)))))


(defn filter-value [attribute value]
  (cond
    (= attribute :group/id) (first value)
    (#{:workspace/assignee :review/assignee :approval/assignee :approval/user-group} attribute) (:db/id value)
    :else value))


(rf/reg-sub ::selectable-filter-conditions
  :<- [::search-config]
  (fn [{:keys [filter-values]}]
    (->> complaint-model/dashboard-filter-condition-attributes
         (keep (fn [attribute]
                 (when-let [vs (->> (get filter-values attribute)
                                    (map (fn [v] (filter-value attribute v)))
                                    seq)]
                   {:filter.condition/attribute attribute
                    :values                     vs}))))))


(rf/reg-sub ::val->str-fn
  :<- [:tr]
  (fn [tr _]
    (fn [attribute]
      (case attribute
        :complaint/category-names identity
        :domain/state (comp tr vector)))))


(rf/reg-sub ::sort-condition :-> ::sort-condition)


(defn find-complaints-cmd [authority-group-ids shape conditions on-success-evt]
  {:comm/command {:command    :complaint/find-complaints
                  :data       (p/assoc-if (p/remove-vals nil? (util/mmap authority-group-ids shape))
                                          :conditions conditions)
                  :method     :post
                  :on-success on-success-evt}})


(defn format-filter-conditions [db]
  (->> db
       ::data
       ::filter-values
       filter-common/filter-values->conditions))


(rf/reg-event-fx ::search
  (fn [{:keys [db]} [_]]
    (p/if-all-let [group-ids (-> db ::data ::authority-group-ids)]
      (find-complaints-cmd group-ids
                           nil
                           (format-filter-conditions db)
                           [::search.success]))))


(rf/reg-event-db ::search.success
  (fn [db [_ _ resp]]
    (assoc-in db [::data ::search-results] resp)))


(rf/reg-event-db ::add-new-search-result
  (fn [db [_ result]]
    (update-in db [::data ::search-results] conj result)))


(rf/reg-event-db ::clear-search-results
  #(p/dissoc-in % [::data ::search-results]))

(defn- severity-with-default [complaint]
  (or (get complaint-model/severity-order (:complaint/severity complaint))
      (get complaint-model/severity-order :complaint-severity/standard)))

(defn sort-by->sort-key+comparator [sort-key]
  (case sort-key
    :search-sorting/severity [severity-with-default <]
    :search-sorting/created-asc [:created <]
    :search-sorting/modified-asc [:modified <]
    :search-sorting/modified-desc [:modified >]
    [:created <]))


(rf/reg-event-db ::set-sort-condition
  (fn [db [_ sort-condition]]
    (assoc db ::sort-condition sort-condition)))


(rf/reg-event-db ::load-more
  (fn [db _]
    (update db ::current-count #(+ % page-limit))))


(rf/reg-sub ::result-total-count
  :<- [::search-results]
  :-> count)


(rf/reg-sub ::current-count :-> ::current-count)


(rf/reg-sub ::search-results
  :<- [::data]
  :<- [::sort-condition]
  (fn [[data sort-condition]]
    (let [sort-key+comparator (sort-by->sort-key+comparator sort-condition)]
      (cond->> (::search-results data)
        sort-key+comparator (sort-by (first sort-key+comparator) (last sort-key+comparator))))))

(rf/reg-sub ::available-permit-types
  :<- [::search-config]
  (fn [search-config]
    (-> search-config :filter-values :complaint/permit-type)))

(rf/reg-sub ::selected-permit-types
  :<- [::filter-values]
  :-> :permit-type)

(defn filter-button
  [{:keys [value current-values tr]}]
  (let [selected? (contains? current-values value)]
    [ct/tag {:label-text (tr [value])
             :selected?  selected?
             :on-click   #(>evt [::set-filter-value
                                 :permit-type
                                 (if selected?
                                   (disj current-values value)
                                   (conj current-values value))])
             :icon       (ui/permit-type-icon value)}]))

(defn filter-buttons [tr]
  (let [permit-types  (<sub [::available-permit-types])
        filter-values (<sub [::selected-permit-types])]
    (when (du/at-least-two? permit-types)
      [:div.flex.flex-row.w-full.px-2.my-2.gap-2
       (for-frag [permit-type permit-types]
         ^{:key (str permit-type)}
         [filter-button (util/mmap {:current-values (or filter-values #{})
                                    :value          permit-type}
                                   tr)])])))


(defn filter-controls [tr]
  (let [config-loading? (<sub [::config-loading?])
        user-filters    (<sub [::user-filters])
        selected-filter (<sub [::selected-saved-filter])]
    [:div
     [eb/error-boundary
      [:div.bg-ui-light-gray.px-4.pb-4.pt-2.rounded
       [filter-common/dashboard-user-filter-controls (util/mmap {:title             :code-enforcement/complaint-filters-title
                                                                 :save-tr-key       :code-enforcement/save-filter
                                                                 :selected-filter   selected-filter
                                                                 :default-filters?  (empty? (<sub [::filter-values]))
                                                                 :saving-filter?    (<sub [::saving-filter?])
                                                                 :select-filter-evt ::select-saved-filter
                                                                 :clear-search-evt  ::clear-search
                                                                 :save-evt          ::save-filter
                                                                 :delete-evt        ::delete-filter}
                                                                tr config-loading? user-filters)]
       (when (ff/enabled? :feature-flag.id/complaints-main-menu-enhancements)
         [filter-buttons tr])
       [:div.flex.flex-wrap.-mx-2.mt-2
        [filter-common/dashboard-filter-selects (util/mmap {:conditions           (<sub [::selectable-filter-conditions])
                                                            :filter-values        (<sub [::filter-values])
                                                            :revision             (<sub [::revision])
                                                            :val->str-fn          (<sub [::val->str-fn])
                                                            :set-filter-value-evt ::set-filter-value
                                                            :light?               true}
                                                           tr config-loading?)]]
       [filter-common/quick-filter-section (util/mmap {:title                 :code-enforcement/complaint-quick-filters
                                                       :quick-filters         (<sub [::user-quick-filters])
                                                       :selected-filter       selected-filter
                                                       :loading?              config-loading?
                                                       :select-filter-evt     ::select-saved-filter
                                                       :save-quick-filter-evt ::save-quick-filter}
                                                      tr user-filters)]]]]))
